/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { Levels } from "@nuance/hierarchical-settings-ui-domain";
import { IHelpTextChangeData } from "../MainView/Panels";
import { FormErrors, FormState, ReadOnlyFormState } from "./Form";

/**
 * Field change information
 */
export interface IFieldChangeData {
    /**
     * Name of field changed
     */
    name: string;

    /**
     * New value of the field
     */
    value?: string;
}

/**
 * Abstract base Commands class
 */
export abstract class BaseCommands<DataT, StateT extends FormState<DataT>> {
    /**
     * Abstract Validate method for performing form validation.
     *
     * @param state State (immutable)
     * @returns Form error object
     */
    abstract Validate(state: StateT): FormErrors<DataT>;

    /**
     * Iterates through the errors to determine if there are no errors.
     *
     * @param state State (immutable)
     * @returns True if there aren't any errors.
     */
    IsValid = (state: ReadOnlyFormState<DataT>): boolean => {
        let k: keyof DataT;

        for (k in state.errors) {
            const error = state.errors[k];
            if (error !== undefined && error !== "") {
                return false;
            }
        }
        return true;
    };

    /**
     * Handles changes on a text field
     *
     * @param state Old state (mutated)
     * @param change Change information
     * @returns New state
     */
    OnTextChangedHandler = (state: StateT, change: IFieldChangeData) => {
        if (change.name !== "" && change.value !== undefined) {
            const k = change.name as keyof DataT;

            state.data = {
                ...state.data,
                [k]: change.value
            };
            state.isDirty = true;
            state.errors = this.Validate(state);
            state.isValid = this.IsValid(state);
        }

        return state;
    };

    /**
     * Handles blur when focus leaves control
     *
     * @param state Old state (mutated)
     * @param name Name of field blurred
     * @returns New state
     */
    OnBlurHandler = (state: StateT, name: string) => {
        if (name !== "") {
            const k = name as keyof DataT;
            state.touched[k] = true;
        }

        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);

        return state;
    };

    /**
     * Handles access level changing
     *
     * @param state Old state
     * @param accessLevels New access level
     * @returns New state
     */
    OnAccessLevelChangedHandler = (state: StateT, accessLevels: Levels) => {
        state.data = {
            ...state.data,
            accessLevels
        };
        state.isDirty = true;
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };

    /**
     * Audit changes checked value changes
     *
     * @param state Old state
     * @param auditChanges Audit changes checked
     * @returns New state
     */
    OnAuditChangedHandler = (state: StateT, auditChanges: boolean) => {
        state.data = {
            ...state.data,
            auditChanges
        };
        state.isDirty = true;
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };

    /**
     * Help text changed
     *
     * @param state Old state
     * @param changeData Help text change data
     * @returns New state
     */
    OnHelpTextChangeHandler = (state: StateT, changeData: IHelpTextChangeData) => {
        state.data = {
            ...state.data,
            tooltip: changeData.tooltipText,
            helper: changeData.helperText,
            helpTextValid: changeData.isValid
        };
        state.isDirty = true;
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);

        return state;
    };
}
